import React from 'react'
import { Box, Stack, TextField, MenuItem, IconButton, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { FormFieldType } from '@/core/models/form.model'
import { t } from '@lingui/macro'

interface Field {
    _id?: string
    label: string
    type: FormFieldType
    required: boolean
    options?: string[]
    category?: string
}

interface FormTemplateFieldRowProps {
    field: Field
    index: number
    onChange: (index: number, field: Partial<Field>) => void
    onRemove: (index: number) => void
}

const FormTemplateFieldRow: React.FC<FormTemplateFieldRowProps> = React.memo(({ field, index, onChange, onRemove }) => {
    return (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" color="text.secondary">
                        {t`Field`} #{index + 1}
                    </Typography>
                    <IconButton onClick={() => onRemove(index)} color="error" size="small">
                        <DeleteIcon />
                    </IconButton>
                </Stack>

                <TextField
                    label={t`Field label`}
                    value={field.label}
                    onChange={(e) => onChange(index, { label: e.target.value })}
                    fullWidth
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <TextField
                        select
                        label={t`Field type`}
                        value={field.type}
                        onChange={(e) => onChange(index, { type: e.target.value as FormFieldType })}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    >
                        {Object.values(FormFieldType).map((type) => (
                            <MenuItem key={type} value={type}>
                                {t`${type}`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label={t`Required`}
                        value={field.required}
                        onChange={(e) => onChange(index, { required: e.target.value === 'true' })}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    >
                        <MenuItem value="true">{t`Yes`}</MenuItem>
                        <MenuItem value="false">{t`No`}</MenuItem>
                    </TextField>
                </Stack>

                {field.type === FormFieldType.Multiple && (
                    <TextField
                        fullWidth
                        label={t`Field options`}
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => {
                            onChange(index, {
                                options: e.target.value.split('\n'),
                            })
                        }}
                        helperText={t`Field options help`}
                        multiline
                        rows={4}
                        placeholder={t`Enter one option per line`}
                    />
                )}
            </Stack>
        </Box>
    )
})

export default FormTemplateFieldRow
